import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '@/store/hooks';
import { selectAuthError, selectToken } from '@/store/slices/authSlice';
import { useAuthActions } from '@/lib/auth/AuthActionsContext';
import {
  getAdminPortalUrl,
  MANAGEMENT_ORGANIZATION,
  normalizeTenantCode,
  parseEntryUrl,
  tenantPath,
} from '@/lib/auth/authFlow';
import {
  clearInteractiveLoginRequest,
  subscribeToInteractiveLogin,
  type InteractiveLoginRequest,
} from '@/lib/auth/interactiveLogin';
import { AuthLoading } from '@/components/auth/AuthLoading';
import { ErrorDisplay } from '@/components/ui/feedback/error-display';

interface AuthenticationControllerProps {
  children: React.ReactNode;
}

type AuthPhase =
  | 'initializing'
  | 'anonymous'
  | 'acquiring-token'
  | 'validating-tenant'
  | 'authenticated'
  | 'error';

/** Single owner of interactive login and post-login navigation. */
export function AuthenticationController({ children }: AuthenticationControllerProps) {
  const {
    isLoading: auth0Loading,
    isAuthenticated: auth0Authenticated,
    user: auth0User,
    error: auth0Error,
  } = useAuth0();
  const { login, logout } = useAuthActions();
  const token = useAppSelector(selectToken);
  const reduxAuthError = useAppSelector(selectAuthError);
  const tenants = useAppSelector((state) => state.tenant.tenants);
  const tenantResolutionStatus = useAppSelector(
    (state) => state.tenant.tenantResolutionStatus,
  );
  const tenantResolutionError = useAppSelector(
    (state) => state.tenant.tenantResolutionError,
  );
  const [interactiveRequest, setInteractiveRequest] =
    useState<InteractiveLoginRequest | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const startedFlowRef = useRef<string | null>(null);
  const startedNavigationRef = useRef<string | null>(null);

  const entry = useMemo(() => parseEntryUrl(window.location), []);
  const tokenTenantCode = normalizeTenantCode(
    auth0User?.tenant_code as string | undefined,
  );

  useEffect(
    () => subscribeToInteractiveLogin(setInteractiveRequest),
    [],
  );

  const phase: AuthPhase = useMemo(() => {
    if (auth0Loading) return 'initializing';
    if (auth0Error || reduxAuthError || flowError) return 'error';
    if (!auth0Authenticated) return 'anonymous';
    if (!token) return 'acquiring-token';
    if (
      tenantResolutionStatus === 'idle' ||
      tenantResolutionStatus === 'loading'
    ) {
      return 'validating-tenant';
    }
    return 'authenticated';
  }, [
    auth0Loading,
    auth0Error,
    reduxAuthError,
    flowError,
    auth0Authenticated,
    token,
    tenantResolutionStatus,
  ]);

  useEffect(() => {
    if (phase === 'initializing' || phase === 'error') return;

    const replaceLocation = (target: string) => {
      if (startedNavigationRef.current) return;
      startedNavigationRef.current = target;
      window.location.replace(target);
    };

    const startLogin = (
      key: string,
      options: Parameters<typeof login>[0],
    ) => {
      if (startedFlowRef.current === key) return;
      startedFlowRef.current = key;
      clearInteractiveLoginRequest();
      login(options).catch((error) => {
        console.error('[AuthenticationController] Login redirect failed:', error);
        startedFlowRef.current = null;
        setFlowError('Could not start the login flow. Please try again.');
      });
    };

    if (entry.hasLoginAsTenantParameter && !entry.loginAsTenantCode) {
      setFlowError('The tenant_code login parameter is invalid.');
      return;
    }

    // Invitation links may use /login or an Auth0-configured application login
    // URI. Consume them wherever they arrive and never preserve the capability
    // parameters in appState.
    if (entry.invitation || entry.invitationOrganization) {
      if (!entry.invitation || !entry.invitationOrganization) {
        setFlowError('This invitation link is incomplete or invalid.');
        return;
      }
      startLogin(`invitation:${entry.invitation}`, {
        organization: entry.invitationOrganization,
        invitation: entry.invitation,
        ...(entry.loginAsTenantCode
          ? { tenantCode: entry.loginAsTenantCode }
          : undefined),
        returnTo: entry.loginAsTenantCode
          ? tenantPath(entry.loginAsTenantCode, '/dashboard')
          : '/',
      });
      return;
    }

    if (entry.isLogin && entry.loginAsTenantCode) {
      startLogin(`management:${entry.loginAsTenantCode}`, {
        organization: MANAGEMENT_ORGANIZATION,
        tenantCode: entry.loginAsTenantCode,
        returnTo: tenantPath(entry.loginAsTenantCode, '/dashboard'),
      });
      return;
    }

    // The callback is exclusively processed by Auth0Provider. It will invoke
    // onRedirectCallback and leave this route; no other code acts on it.
    if (entry.isAuthCallback) return;

    if (phase === 'anonymous') {
      if (entry.isPublic && !entry.isLogin) return;
      if (!entry.isBareApplicationPath && !entry.requestedTenantCode) {
        setFlowError('The tenant code in the URL is invalid.');
        return;
      }
      const organization = entry.requestedTenantCode ?? tokenTenantCode;
      startLogin(organization ? `tenant:${organization}` : 'default', {
        ...(organization ? { organization } : undefined),
        returnTo: entry.pathWithQueryAndHash,
      });
      return;
    }

    if (!tokenTenantCode) {
      setFlowError('The Auth0 identity does not contain a valid tenant_code claim.');
      return;
    }

    if (tokenTenantCode === MANAGEMENT_ORGANIZATION) {
      replaceLocation(getAdminPortalUrl());
      return;
    }

    if (interactiveRequest) {
      startLogin(`recovery:${interactiveRequest.reason}:${tokenTenantCode}`, {
        organization: entry.requestedTenantCode ?? tokenTenantCode,
        returnTo: entry.isLogin ? '/' : entry.pathWithQueryAndHash,
      });
      return;
    }

    if (entry.isLogin) {
      replaceLocation(tenantPath(tokenTenantCode, '/dashboard'));
      return;
    }
    if (entry.isPublic) return;

    if (entry.isRoot || entry.isBareApplicationPath) {
      replaceLocation(tenantPath(tokenTenantCode, entry.pathWithQueryAndHash));
      return;
    }

    if (entry.requestedTenantCode !== tokenTenantCode) {
      if (!entry.requestedTenantCode) {
        setFlowError('The tenant code in the URL is invalid.');
        return;
      }
      startLogin(`tenant:${entry.requestedTenantCode}`, {
        organization: entry.requestedTenantCode,
        returnTo: entry.pathWithQueryAndHash,
      });
    }
  }, [
    entry,
    interactiveRequest,
    login,
    phase,
    tokenTenantCode,
  ]);

  const error = flowError ?? auth0Error?.message ?? reduxAuthError;
  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-tertiary">
        <ErrorDisplay
          icon="warning"
          title="Authentication Error"
          message={error}
          retryText="Start new login"
          onRetry={() => {
            window.history.replaceState({}, '', '/login');
            window.location.reload();
          }}
          secondaryAction={{ label: 'Sign out', onClick: () => void logout() }}
          fullPage={false}
        />
      </div>
    );
  }

  if (entry.isPublic && !entry.isLogin && !entry.isAuthCallback) {
    return <>{children}</>;
  }

  // Login/navigation always takes precedence over tenant-query errors. During
  // a tenant switch the old token's in-flight membership request is commonly
  // aborted by the full-page Auth0 redirect and surfaces as FETCH_ERROR. It
  // must never flash an error while the new organization login is underway.
  if (
    startedFlowRef.current ||
    startedNavigationRef.current ||
    phase !== 'authenticated' ||
    entry.isAuthCallback ||
    entry.isLogin ||
    entry.isRoot ||
    entry.isBareApplicationPath ||
    entry.requestedTenantCode !== tokenTenantCode
  ) {
    return <AuthLoading />;
  }

  if (tenantResolutionStatus === 'error') {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-tertiary">
        <ErrorDisplay
          icon="warning"
          title="Unable to validate tenant"
          message={tenantResolutionError ?? 'Tenant membership could not be loaded.'}
          retryText="Try Again"
          onRetry={() => window.location.reload()}
          secondaryAction={{ label: 'Sign out', onClick: () => void logout() }}
          fullPage={false}
        />
      </div>
    );
  }

  const membershipIsValid = tenants.some(
    (tenant) => normalizeTenantCode(tenant.code) === tokenTenantCode,
  );
  if (!membershipIsValid) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-tertiary">
        <ErrorDisplay
          icon="warning"
          title="Tenant access unavailable"
          message="The tenant in your Auth0 identity is not present in your tenant memberships."
          retryText="Sign in again"
          onRetry={() =>
            void login({
              organization: tokenTenantCode ?? undefined,
              returnTo: entry.pathWithQueryAndHash,
            })
          }
          secondaryAction={{ label: 'Sign out', onClick: () => void logout() }}
          fullPage={false}
        />
      </div>
    );
  }

  return <>{children}</>;
}
